import { ShapeUtils, Vector2, Vector3 } from 'three';
import HalfEdgeMesh from '@three/common/halfEdgeMesh/HalfEdgeMesh';
import { Vertex } from '@/thirdPartyLibraries/three-mesh-halfedge/src';

/**
 * Seals the OUTER free border loop of a mesh with a flat cap.
 *
 * Pipeline (v1, behind a test button): isolate the largest border loop → collapse it
 * down to ~`keepFraction` of its vertices → Laplacian-smooth it → drop it onto a single
 * Y plane → triangulate the (now planar) polygon in the XZ plane (earcut) and append the
 * cap triangles by rebuilding the geometry.
 *
 * IMPORTANT: the fill rebuilds the half-edge data structure (setNewGeometry), so every
 * existing `Vertex` reference is replaced. Callers that hold vertex-keyed bindings (e.g.
 * the gum→tooth seam maps in JawCorrection) MUST re-establish them after calling this.
 *
 * Known v1 limitation: only the single largest loop is sealed. Where a scan is short at the
 * last molars the outer perimeter and a tooth loop merge into one loop — that merged loop is
 * what gets sealed, so the cap may reach behind the last tooth. Tracked as a follow-up.
 */
export interface SealOuterLoopOptions {
  /** Fraction of the loop's vertices to keep when collapsing. 0.2 ≈ reduce to 20%. 1 disables. */
  keepFraction?: number;
  /** Laplacian smoothing passes over the loop before flattening. */
  smoothIterations?: number;
  /** Smoothing strength per pass (0..1). */
  smoothStrength?: number;
  /**
   * Plane the loop is dropped onto. Min variants cap below (lower jaw),
   * max variants cap above (upper jaw, whose free rim points up).
   */
  flattenMode?: 'borderMin' | 'meshMin' | 'borderMax' | 'meshMax' | number;
  /** Manually invert the cap winding (use if the cap comes out inside-out / unsealed). */
  flipWinding?: boolean;
  /**
   * Triangulate the flattened loop into a flat cap (default). `false` keeps the
   * collapse → smooth → flatten pipeline but leaves the loop open — used for
   * hollow-shell exports where the bottom must stay open.
   */
  cap?: boolean;
  /**
   * After flattening, smooth this many vertex rings above the rim so the
   * descending wall loses raw scan-edge noise (0 disables). The rim itself
   * stays pinned on the plane.
   */
  wallSmoothRings?: number;
}

export interface SealOuterLoopResult {
  loopLength: number;
  trianglesAdded: number;
  /** Heuristic: did the free-border count drop by roughly the loop length? */
  sealed: boolean;
  /** Local-space Y of the flatten plane the rim was dropped onto (when resolved). */
  planeY?: number;
}

/** Perimeter (closed) of an ordered vertex loop. */
function loopPerimeter(loop: Vertex[]): number {
  let sum = 0;
  for (let i = 0; i < loop.length; i++) {
    sum += loop[i].position.distanceTo(loop[(i + 1) % loop.length].position);
  }
  return sum;
}

/** The largest border loop by perimeter (the outer perimeter in well-scanned cases). */
function largestBorderLoop(mesh: HalfEdgeMesh): Vertex[] {
  const loops = mesh.getBorderLoops();
  let best: Vertex[] = [];
  let bestPerim = -Infinity;
  for (const loop of loops) {
    if (loop.length < 3) continue;
    const perim = loopPerimeter(loop);
    if (perim > bestPerim) {
      bestPerim = perim;
      best = loop;
    }
  }
  return best;
}

/** Every-other border edge of the loop, skipping vertices already claimed by a pair. */
function collectCollapsePairs(loop: Vertex[]): Array<[Vertex, Vertex]> {
  const L = loop.length;
  const used = new Set<number>();
  const pairs: Array<[Vertex, Vertex]> = [];
  // Sample every other edge so one pass roughly halves the loop.
  for (let i = 0; i < L - 1; i += 2) {
    const a = loop[i];
    const b = loop[(i + 1) % L];
    if (used.has(a.id) || used.has(b.id)) continue;
    const he = a.getHalfedgeToVertex(b);
    // Only collapse genuine border edges (one side is a hole).
    if (!he || (he.face && he.twin?.face)) continue;
    pairs.push([a, b]);
    used.add(a.id);
    used.add(b.id);
  }
  return pairs;
}

/** Collapse the outer loop down to ~keepFraction of its vertices (in place). */
function collapseOuterLoop(mesh: HalfEdgeMesh, keepFraction: number): void {
  const ds = mesh.halfEdges;
  const start = largestBorderLoop(mesh).length;
  if (start < 6) return;
  const target = Math.max(6, Math.floor(start * keepFraction));

  for (let guard = 0; guard < 64; guard++) {
    const loop = largestBorderLoop(mesh);
    const L = loop.length;
    if (L <= target) break;

    const pairs = collectCollapsePairs(loop);
    if (pairs.length === 0) break;

    if (collapsePairs(ds, pairs, L, target) === 0) break;
  }
  mesh.calculateBorderVertices();
}

/**
 * Collapses sampled border pairs until the loop would reach `target` vertices.
 * Each successful collapse shortens the loop by exactly one vertex, so a
 * counter replaces re-deriving the loop (a full-mesh scan) per collapse.
 * Returns the number of edges collapsed.
 */
function collapsePairs(
  ds: HalfEdgeMesh['halfEdges'],
  pairs: Array<[Vertex, Vertex]>,
  loopLength: number,
  target: number,
): number {
  let remaining = loopLength;
  let collapsed = 0;
  for (const [a, b] of pairs) {
    if (remaining <= target) break;
    if (ds.edgeCollapse(a, b, true) || ds.edgeCollapse(b, a, true)) {
      collapsed++;
      remaining--;
    }
  }
  return collapsed;
}

/** Laplacian smoothing along the loop (toward the midpoint of its two neighbours). */
function smoothLoop(loop: Vertex[], iterations: number, strength: number): void {
  const L = loop.length;
  if (L < 3) return;
  for (let it = 0; it < iterations; it++) {
    const next = loop.map((v, i) => {
      const prev = loop[(i - 1 + L) % L].position;
      const nxt = loop[(i + 1) % L].position;
      const mid = prev.clone().add(nxt).multiplyScalar(0.5);
      return v.position.clone().lerp(mid, strength);
    });
    for (let i = 0; i < L; i++) loop[i].position.copy(next[i]);
  }
}

function resolveTargetY(
  mesh: HalfEdgeMesh,
  loop: Vertex[],
  mode: 'borderMin' | 'meshMin' | 'borderMax' | 'meshMax' | number,
): number {
  if (typeof mode === 'number') return mode;
  const takeMax = mode === 'borderMax' || mode === 'meshMax';
  const source = mode === 'meshMin' || mode === 'meshMax' ? mesh.halfEdges.vertices : loop;
  let best = takeMax ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY;
  for (const v of source) {
    best = takeMax ? Math.max(best, v.position.y) : Math.min(best, v.position.y);
  }
  return Number.isFinite(best) ? best : 0;
}

/**
 * Smooths the wall band next to the (already flattened) rim: the flatten moves
 * only the rim vertices, so the rows above keep raw scan-edge noise and the
 * descending wall comes out as jagged stairs. Rim (ring 0) stays pinned on the
 * plane; rings 1..`rings` relax toward their neighbour average.
 */
/**
 * Bounded one-ring: Vertex.adjacentVertices walks `twin.next` until it returns
 * to the start halfedge — on a fan corrupted by edge collapses (parallel edges
 * from ensureFreeOutHalfedge) that orbit may never close, hanging the export.
 * This walk gives up after `cap` steps instead.
 */
function boundedNeighbors(v: Vertex, cap = 128): Vertex[] {
  const out: Vertex[] = [];
  const start = v.halfedge;
  if (!start || start.vertex !== v) return out;
  let curr = start;
  for (let i = 0; i < cap; i++) {
    out.push(curr.twin.vertex);
    curr = curr.twin.next;
    if (curr === start) break;
  }
  return out;
}

function collectRingBandAroundLoop(loop: Vertex[], rings: number): Vertex[] {
  const visited = new Set<Vertex>(loop);
  const band: Vertex[] = [];
  let frontier = loop;
  for (let r = 1; r <= rings; r++) {
    const next: Vertex[] = [];
    for (const v of frontier) {
      for (const n of boundedNeighbors(v)) {
        if (!visited.has(n)) {
          visited.add(n);
          next.push(n);
          band.push(n);
        }
      }
    }
    frontier = next;
  }
  return band;
}

/**
 * Decimates the wall band next to the rim: collapseOuterLoop leaves the rim at
 * ~20 % density while the rows above keep full scan density — that mismatch
 * (long rim edges meeting many short band edges) is what makes the descending
 * wall jagged. Collapses band-internal edges shorter than `targetEdgeMm` so the
 * band density grades toward the rim's.
 */
function decimateWallBand(mesh: HalfEdgeMesh, loop: Vertex[], rings: number, targetEdgeMm: number): void {
  const ds = mesh.halfEdges;
  const band = collectRingBandAroundLoop(loop, rings);
  const bandSet = new Set(band);
  const targetSq = targetEdgeMm * targetEdgeMm;

  // Collapse ONE short band-internal edge at v; returns the removed neighbour.
  // Edges leaving the band (to the rim or the untouched gum) are never touched.
  const collapseShortEdgeAt = (v: Vertex): Vertex | null => {
    for (const n of boundedNeighbors(v)) {
      if (!bandSet.has(n)) continue;
      if (v.position.distanceToSquared(n.position) >= targetSq) continue;
      if (ds.edgeCollapse(n, v, true)) return n;
    }
    return null;
  };

  for (let pass = 0; pass < 4; pass++) {
    let collapsed = 0;
    for (const v of band) {
      if (!bandSet.has(v) || !ds.vertices.includes(v)) continue;
      const removed = collapseShortEdgeAt(v);
      if (removed) {
        bandSet.delete(removed);
        collapsed++;
      }
    }
    if (collapsed === 0) break;
  }
  mesh.calculateBorderVertices();
}

function smoothWallBandAroundLoop(
  loop: Vertex[],
  rings: number,
  iterations: number,
  lambda = 0.5,
): void {
  const band = collectRingBandAroundLoop(loop, rings);
  if (band.length === 0) return;

  const avg = new Vector3();
  const targets = new Map<Vertex, Vector3>();
  for (let iter = 0; iter < iterations; iter++) {
    targets.clear();
    for (const v of band) {
      avg.set(0, 0, 0);
      let count = 0;
      for (const n of boundedNeighbors(v)) {
        avg.add(n.position);
        count++;
      }
      if (count < 2) continue;
      avg.divideScalar(count);
      targets.set(v, v.position.clone().lerp(avg, lambda));
    }
    targets.forEach((pos, v) => v.position.copy(pos));
  }
}

/** Fan triangulation fallback (indices into the ordered loop) for degenerate earcut cases. */
function fanTriangulation(n: number): number[][] {
  const tris: number[][] = [];
  for (let i = 1; i < n - 1; i++) tris.push([0, i, i + 1]);
  return tris;
}

/** True if the loop's boundary half-edge runs loop[0]→loop[1] (face on the twin side). */
function boundaryRunsForward(loop: Vertex[]): boolean {
  const he = loop[0].getHalfedgeToVertex(loop[1]);
  if (he) return he.face === null;
  const back = loop[1].getHalfedgeToVertex(loop[0]);
  return back ? back.face !== null : false;
}

export function sealOuterBorderLoop(
  mesh: HalfEdgeMesh,
  opts: SealOuterLoopOptions = {},
): SealOuterLoopResult {
  const keepFraction = Math.min(1, Math.max(0.02, opts.keepFraction ?? 0.2));
  const smoothIterations = Math.max(0, Math.floor(opts.smoothIterations ?? 4));
  const smoothStrength = Math.min(1, Math.max(0, opts.smoothStrength ?? 0.5));
  const flattenMode = opts.flattenMode ?? 'borderMin';

  mesh.calculateBorderVertices();
  let loop = largestBorderLoop(mesh);
  if (loop.length < 3) {
    return { loopLength: loop.length, trianglesAdded: 0, sealed: false };
  }

  // 1) collapse → 2) smooth → 3) flatten
  if (keepFraction < 0.999) {
    collapseOuterLoop(mesh, keepFraction);
    loop = largestBorderLoop(mesh);
    if (loop.length < 3) return { loopLength: loop.length, trianglesAdded: 0, sealed: false };
  }
  smoothLoop(loop, smoothIterations, smoothStrength);
  const targetY = resolveTargetY(mesh, loop, flattenMode);
  for (const v of loop) v.position.y = targetY;

  const wallSmoothRings = Math.max(0, Math.floor(opts.wallSmoothRings ?? 8));
  if (wallSmoothRings > 0) {
    // Decimate first (grades full scan density toward the sparse collapsed
    // rim), then smooth what remains of the band.
    const meanEdge = Math.max(mesh.halfEdges.getMeanEdgeLength?.() ?? 0.2, 1e-6);
    decimateWallBand(mesh, loop, wallSmoothRings, 2.5 * meanEdge);
    loop = largestBorderLoop(mesh);
    smoothWallBandAroundLoop(loop, wallSmoothRings, 10);
  }

  if (opts.cap === false) {
    // Flatten-only mode: bake the collapsed/flattened DS, keep the loop open.
    mesh.setNewGeometry(mesh.toBufferGeometry());
    return { loopLength: loop.length, trianglesAdded: 0, sealed: false, planeY: targetY };
  }

  // 4) winding: cap perimeter edges must oppose the gum boundary half-edges so the rebuilt
  //    DS pairs them as twins (otherwise the seam stays open). flipWinding lets the tester
  //    correct it by eye if the heuristic guesses wrong on a given scan.
  const reverse = boundaryRunsForward(loop) !== Boolean(opts.flipWinding);
  const ordered = reverse ? [...loop].reverse() : loop;

  // 5) earcut in the XZ plane (the loop is now planar in Y)
  const contour = ordered.map((v) => new Vector2(v.position.x, v.position.z));
  let triangles: number[][];
  try {
    triangles = ShapeUtils.triangulateShape(contour, []);
    if (!triangles || triangles.length === 0) triangles = fanTriangulation(ordered.length);
  } catch {
    triangles = fanTriangulation(ordered.length);
  }

  // Bake the (edited) half-edge mesh to geometry, mirroring toBufferGeometry's vertex order
  // so we can address loop vertices by their buffer index.
  const vertexToIndex = new Map<Vertex, number>();
  let idx = 0;
  for (const v of mesh.halfEdges.vertices) vertexToIndex.set(v, idx++);

  const geometry = mesh.toBufferGeometry();
  const indexAttr = geometry.getIndex();
  const index: number[] = indexAttr ? Array.from(indexAttr.array as ArrayLike<number>) : [];

  let trianglesAdded = 0;
  for (const tri of triangles) {
    const ia = vertexToIndex.get(ordered[tri[0]]);
    const ib = vertexToIndex.get(ordered[tri[1]]);
    const ic = vertexToIndex.get(ordered[tri[2]]);
    if (ia === undefined || ib === undefined || ic === undefined) continue;
    index.push(ia, ib, ic);
    trianglesAdded++;
  }
  geometry.setIndex(index);

  const borderBefore = mesh.getBorderVertices().length;
  mesh.setNewGeometry(geometry); // rebuilds the DS + recomputes border vertices
  const borderAfter = mesh.getBorderVertices().length;
  const sealed = borderBefore - borderAfter >= Math.floor(loop.length * 0.5);

  return { loopLength: loop.length, trianglesAdded, sealed, planeY: targetY };
}
